import { ArrowLeftRight, BarChart3, Calculator, Check, FileText, X } from "lucide-react";
import { FadeUp, MaskedReveal } from "./Reveal";

const APP_URL = "https://app.meerjarenplanonline.nl";
const APP_TRIAL_URL = `${APP_URL}/login`;
const APP_PRIJZEN_URL = `${APP_URL}/prijzen`;

// Telt kliks op "Proef starten" (zichtbaar in /statistieken); mag navigatie nooit blokkeren.
// Eigen kliks herkennen: surf met ?bron=<naam> in de URL, dan komt die mee als label.
const trackTrialStart = () => {
    try {
        const bron = new URLSearchParams(window.location.search).get("bron");
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/trial-start`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ source: bron ? `pricing-trial · ${bron.slice(0, 40)}` : "pricing-trial" }),
            keepalive: true,
        }).catch(() => {});
    } catch { /* negeren */ }
};

const TIERS = [
    {
        id: "trial",
        name: "14-daagse proef",
        badge: null,
        price: "Gratis",
        per: "",
        sub: "Speel vrij met de Demo Watersportclub",
        cta: "Proef starten",
        cardClass: "bg-white border border-black/10",
        labelClass: "text-[#0047FF]",
        subClass: "text-[#0A0A0A]/50",
        perClass: "text-[#0A0A0A]/40",
        ctaClass: "bg-[#0047FF] hover:bg-[#0036CC] text-white",
        checkClass: "text-[#0047FF]",
        textClass: "text-[#0A0A0A]/75",
        textOffClass: "text-[#0A0A0A]/40",
        features: [
            { text: "Registreer met alleen je e-mail", ok: true },
            { text: "Vooraf gevulde Demo Watersportclub (2020–2024 data + afschrijvingsstaat)", ok: true },
            { text: "Elke functie werkt — ook Premium", ok: true },
        ],
    },
    {
        id: "standard",
        name: "Standaard",
        badge: null,
        price: "€99",
        wasPrice: "€129",
        per: "/jaar",
        sub: "Voor het jaarlijkse beleidsplan",
        cta: "Kies Standaard",
        cardClass: "bg-[#0A0A0A] text-white border-2 border-[#0047FF] blue-glow",
        labelClass: "text-[#7ea2ff]",
        subClass: "text-white/60",
        perClass: "text-white/50",
        ctaClass: "bg-[#FF6B00] hover:bg-[#e05e00] text-white",
        checkClass: "text-[#4d79ff]",
        textClass: "text-white/80",
        textOffClass: "text-white/50",
        features: [
            { text: "Tot 3 clubs met historische data", ok: true },
            { text: "Onbeperkt scenario's per club", ok: true },
            { text: "Meerdere scenario's naast elkaar vergelijken", ok: true },
            { text: "Sjabloon-download + Excel-import + Excel-export", ok: true },
            { text: "Detail import/export met subposten", ok: true },
            { text: "Kopiëren + Vergrendelen (safety)", ok: true },
            { text: "PDF-export scenario (met cover & grafieken)", ok: true },
            { text: "AI-import jaarverslag (PDF)", ok: true },
            { text: "NPV-modules (NPV, IRR, terugverdientijd)", ok: false },
            { text: "Monte Carlo simulatie (tornado, fan-chart)", ok: false },
            { text: "Multi-user & rollen (viewer / editor)", ok: false },
        ],
    },
    {
        id: "premium",
        name: "Premium",
        badge: null,
        price: "€129",
        wasPrice: "€159",
        per: "/jaar",
        sub: "Voor besturen en adviseurs die een volledig beeld wensen.",
        cta: "Kies Premium",
        cardClass: "bg-[#0A0A0A] text-white border-2 border-[#FF6B00] orange-glow",
        labelClass: "text-[#FF8A3D]",
        subClass: "text-white/60",
        perClass: "text-white/50",
        ctaClass: "bg-[#0047FF] hover:bg-[#0036CC] text-white",
        checkClass: "text-[#FF6B00]",
        textClass: "text-white/80",
        textOffClass: "text-white/50",
        features: [
            { text: "Tot 5 clubs met historische data", ok: true },
            { text: "Onbeperkt scenario's per club", ok: true },
            { text: "Meerdere scenario's naast elkaar vergelijken", ok: true },
            { text: "Sjabloon-download + Excel-import + Excel-export", ok: true },
            { text: "Detail import/export met subposten", ok: true },
            { text: "Kopiëren + Vergrendelen (safety)", ok: true },
            { text: "PDF-export scenario (met cover & grafieken)", ok: true },
            { text: "AI-import jaarverslag (PDF)", ok: true },
            { text: "NPV-modules (NPV, IRR, terugverdientijd)", ok: true },
            { text: "Monte Carlo simulatie (tornado, fan-chart)", ok: true },
            { text: "Multi-user & rollen (viewer / editor)", ok: true },
        ],
    },
];

const CHIPS = [
    { icon: ArrowLeftRight, title: "Vergelijken", desc: "Naast elkaar scenario's vergelijken op het effect op de kasstroom.", tier: "Standaard+" },
    { icon: FileText, title: "AI PDF-import", desc: "Upload het jaarverslag en de AI vult de historische data in.", tier: "Standaard+" },
    { icon: Calculator, title: "NPV-modules", desc: "NPV, IRR en terugverdientijd per investeringsplan én scenario-breed.", tier: "Premium" },
    { icon: BarChart3, title: "Monte Carlo", desc: "Kansverdelingen, tornado-grafiek en fan-chart voor risico-analyse.", tier: "Premium" },
];

const PRICING_FAQS = [
    {
        q: "Hoe werkt de proef?",
        a: "14 dagen gratis, geen creditcard. Je krijgt een vooraf gevulde Demo Watersportclub (5 jaar historische data + afschrijvingen). Elke functie werkt — inclusief Monte Carlo en PDF-export. Na 14 dagen kies je Standaard of Premium.",
    },
    {
        q: "Kan ik upgraden of downgraden?",
        a: "Ja, op elk moment. Bij een upgrade betaal je enkel het verschil, pro rata. Bij een downgrade gaat de nieuwe tier in bij de eerstvolgende verlenging.",
    },
    {
        q: "Wat gebeurt er met mijn data als ik stop?",
        a: "Je data blijft nog 90 dagen bewaard zodat je 'm kunt exporteren (Excel + PDF). Daarna wordt alles definitief verwijderd.",
    },
    {
        q: "Meer dan 5 clubs of meerjarige korting?",
        a: "Standaard geeft toegang tot 3 clubs, Premium tot 5. Heb je een grotere portefeuille of wil je meerjarige korting? We maken graag een offerte op maat — neem contact op via info@club-fin.nl.",
    },
];

const TierCard = ({ tier }) => (
    <FadeUp
        data-testid={`pricing-card-${tier.id}`}
        className={`relative rounded-2xl p-8 flex flex-col ${tier.cardClass}`}
    >
        {tier.badge && (
            <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#0047FF] text-white text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-1.5 whitespace-nowrap">
                {tier.badge}
            </span>
        )}
        <p className={`text-xs tracking-[0.2em] uppercase font-bold ${tier.labelClass}`}>
            {tier.name}
        </p>
        <p className={`mt-2 text-sm ${tier.subClass}`}>{tier.sub}</p>
        {tier.wasPrice ? (
            <div className="mt-5 flex items-center gap-4">
                <div
                    data-testid={`pricing-intro-${tier.id}`}
                    className={`shrink-0 w-24 h-24 rounded-full flex flex-col items-center justify-center font-heading font-black text-white ${
                        tier.id === "premium" ? "bg-[#FF6B00] orange-glow" : "bg-[#0047FF] blue-glow"
                    }`}
                >
                    <span className="text-2xl leading-none tracking-tight">{tier.price}</span>
                    <span className="text-[10px] font-bold mt-0.5 opacity-80">p/jr</span>
                </div>
                <div>
                    <p className={`text-[11px] font-bold tracking-[0.15em] uppercase ${tier.labelClass}`}>Introductieprijs</p>
                    <p className="mt-1 text-white/60 text-sm">
                        i.p.v. <span className="line-through">{tier.wasPrice}</span> p/jr
                    </p>
                    <p className="mt-1 text-[10px] text-white/40">geldig voor het eerste jaar</p>
                </div>
            </div>
        ) : (
            <p className="mt-5 font-heading font-black tracking-tight">
                <span className="text-5xl">{tier.price}</span>
                {tier.per && <span className={`text-lg font-bold ${tier.perClass}`}>{tier.per}</span>}
            </p>
        )}
        <a
            data-testid={`pricing-cta-${tier.id}`}
            href={tier.id === "trial" ? APP_TRIAL_URL : APP_PRIJZEN_URL}
            onClick={tier.id === "trial" ? trackTrialStart : undefined}
            className={`mt-7 inline-flex items-center justify-center rounded-full font-bold text-sm px-6 py-3.5 transition-[background-color,transform] duration-300 hover:scale-[1.03] ${tier.ctaClass}`}
        >
            {tier.cta}
        </a>
        <ul className="mt-8 space-y-3.5">
            {tier.features.map((f) => (
                <li key={f.text} className={`flex items-start gap-3 text-sm ${f.ok ? "" : "opacity-40"}`}>
                    {f.ok ? (
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.checkClass}`} />
                    ) : (
                        <X className="w-4 h-4 mt-0.5 shrink-0" />
                    )}
                    <span className={f.ok ? tier.textClass : `${tier.textOffClass} line-through`}>
                        {f.text}
                    </span>
                </li>
            ))}
        </ul>
    </FadeUp>
);

const Pricing = () => (
    <section id="prijzen" data-testid="pricing-section" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-3xl mb-16">
                <FadeUp>
                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#FF6B00] mb-5">Abonnementen — jaarlijkse tarieven</p>
                </FadeUp>
                <h2 className="font-heading text-[#0A0A0A] font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
                    <MaskedReveal>Kies wat past</MaskedReveal>
                    <MaskedReveal delay={0.12}>bij <span className="text-[#0047FF]">jouw club.</span></MaskedReveal>
                </h2>
                <FadeUp delay={0.2} className="mt-6">
                    <p className="text-[#0A0A0A]/60 text-base sm:text-lg leading-relaxed max-w-2xl">
                        MeerjarenplanOnline.nl wordt meestal 1 of 2 keer per jaar intensief gebruikt — rondom het
                        beleidsplan of de jaarbegroting. Daarom rekenen we alleen een jaarbedrag,
                        geen maandelijkse abonnementen.
                    </p>
                </FadeUp>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                {TIERS.map((t) => (
                    <TierCard key={t.id} tier={t} />
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
                {CHIPS.map((chip, i) => {
                    const Icon = chip.icon;
                    return (
                        <FadeUp key={chip.title} delay={i * 0.07} className="rounded-2xl border border-black/10 bg-[#F9FAFB] p-6">
                            <div className="flex items-center justify-between mb-4">
                                <span className="w-10 h-10 rounded-xl bg-[#0A0A0A] flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-white" />
                                </span>
                                <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-3 py-1 rounded-full ${
                                    chip.tier === "Premium" ? "bg-[#FF6B00]/10 text-[#FF6B00]" : "bg-[#0047FF]/10 text-[#0047FF]"
                                }`}>
                                    {chip.tier}
                                </span>
                            </div>
                            <p className="font-heading text-[#0A0A0A] font-bold tracking-tight text-lg">{chip.title}</p>
                            <p className="mt-1.5 text-sm text-[#0A0A0A]/55 leading-relaxed">{chip.desc}</p>
                        </FadeUp>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 mt-20">
                {PRICING_FAQS.map((f, i) => (
                    <FadeUp key={f.q} delay={i * 0.06} className="border-l-2 border-[#FF6B00] pl-6 py-6 border-b border-b-black/10">
                        <p data-testid={`pricing-faq-${i}`} className="font-heading text-[#0A0A0A] font-bold tracking-tight text-lg">{f.q}</p>
                        <p className="mt-2.5 text-sm text-[#0A0A0A]/60 leading-relaxed">{f.a}</p>
                    </FadeUp>
                ))}
            </div>

            <FadeUp delay={0.1} className="mt-14">
                <p data-testid="pricing-disclaimer" className="text-center text-xs text-[#0A0A0A]/40 max-w-2xl mx-auto leading-relaxed">
                    De gratis proef met de Demo Watersportclub is altijd beschikbaar. Introductieprijzen gelden voor het eerste jaar; daarna €129 (Standaard) resp. €159 (Premium) per jaar. Prijzen zijn incl. BTW, betaling via iDEAL of automatische incasso.
                </p>
            </FadeUp>
        </div>
    </section>
);

export default Pricing;
